/**
 * SEO Configuration for KeyQuest Platform
 * Central configuration for all SEO-related settings, templates, and constants
 */

// Site-wide settings
export const SITE_CONFIG = {
    siteName: 'KeyQuest',
    siteUrl: process.env.REACT_APP_SITE_URL || 'https://keyquest.io',
    defaultTitle: 'KeyQuest | VR SEO Intelligence Tools Powered by AI & Big Data',
    defaultDescription: 'Track VR app rankings, analyze performance across VR and gaming platforms, and optimize your app store presence with KeyQuest\'s AI-powered analytics platform.',
    defaultImage: '/new_web_logo.png',
    twitterHandle: '@keyquest',
    author: 'KeyQuest Team',
};

// Structured Data Templates
export const STRUCTURED_DATA = {
    organization: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'KeyQuest',
        description: 'Keyquest | VR SEO Intelligence Tools Powered by AI & Big Data',
        url: SITE_CONFIG.siteUrl,
        logo: `${SITE_CONFIG.siteUrl}/new_web_logo.png`,
        sameAs: [
            // Add your social media URLs here
            // 'https://twitter.com/keyquest',
            // 'https://linkedin.com/company/keyquest'
        ],
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Support',
            // email: 'support@keyquest.com'
        },
    },

    website: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'KeyQuest',
        url: SITE_CONFIG.siteUrl,
        potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_CONFIG.siteUrl}/keyword-search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
        },
    },
};

// Page-specific SEO templates
export const PAGE_SEO = {
    // Landing Page
    landing: {
        title: 'KeyQuest | VR SEO Intelligence Tools Powered by AI & Big Data',
        description: 'Track VR app rankings, analyze performance across VR and gaming platforms, and optimize your app store presence with KeyQuest\'s AI-powered analytics platform.',
        keywords: 'VR analytics, multi-platform analytics, VR ASO, app store optimization, VR game tracking, VR app insights, keyword research, VR market intelligence',
        robots: 'index, follow',
    },

    // Dashboard
    dashboard: {
        title: 'KeyQuest Dashboard | Multi-Platform VR & Gaming Analytics',
        description: 'Track app rankings, downloads, and revenue across all your VR and gaming platforms in one AI-powered analytics dashboard from KeyQuest.',
        keywords: 'VR analytics, multi-platform app tracking, cross-platform analytics, cross-platform ASO, app store optimization, gaming platform analytics',
        robots: 'noindex, follow', // Private dashboard
    },

    // Market Research
    keywordSearch: {
        title: 'VR Keyword Research Tool | Multi-Platform ASO',
        description: 'Find the best keywords for your VR app with KeyQuest\'s keyword research tool. Analyze search volume, competition, and trends across platforms.',
        keywords: 'VR keyword research, ASO keyword tool, VR app keywords, keyword analysis, app store optimization',
        robots: 'index, follow',
    },

    categoryAnalysis: {
        title: 'Genre Explorer | VR App Categories',
        description: 'Analyze VR app categories across platforms. Track category rankings, performance metrics, and discover trending categories.',
        keywords: 'VR app categories, VR genre analytics, category analytics, VR category rankings, app store categories',
        robots: 'index, follow',
    },

    publisherAnalysis: {
        title: 'Publisher Explorer | VR App Developers',
        description: 'Analyze VR game publishers and developers. Track publisher performance, app portfolios, and market insights across platforms.',
        keywords: 'VR game publishers, VR app developers, VR app developer tools, developer analytics, VR publisher insights',
        robots: 'index, follow',
    },

    // Tracking
    keywordTracking: {
        title: 'VR Keyword Rank Tracking | Monitor App Rankings',
        description: 'Track your VR app\'s keyword rankings across platforms. Monitor daily rank changes, analyze competitors, and optimize your ASO strategy.',
        keywords: 'keyword rank tracking, VR keyword tracking, ASO tracking, VR app rankings, app store rank monitoring',
        robots: 'noindex, follow', // User-specific tracking data
    },

    categoryTracking: {
        title: 'VR Category Rank Tracking | Monitor App Store Categories',
        description: 'Track your VR app\'s category rankings across platforms. Monitor category performance and optimize your app store positioning.',
        keywords: 'category rank tracking, VR category tracking, app store categories, VR app category rankings',
        robots: 'noindex, follow', // User-specific tracking data
    },

    appTracking: {
        title: 'VR App Performance Tracking | Monitor App Analytics',
        description: 'Track and analyze VR app performance with KeyQuest\'s comprehensive tracking tools. Monitor downloads, revenue, reviews, and rankings.',
        keywords: 'VR app tracking, app performance monitoring, multi-platform analytics, app tracking tools',
        robots: 'noindex, follow', // User-specific tracking data
    },

    // Meta Insights (Public Pages)
    topApps: {
        title: 'Top VR Apps of {DATE} | Best VR Games',
        description: 'Discover the top-rated VR apps and best VR games across platforms, updated daily with rankings, reviews, and performance data from KeyQuest\'s analytics engine.',
        keywords: 'top VR apps, best VR games, top VR platform games, VR app rankings, popular VR games',
        robots: 'index, follow',
    },

    trending: {
        title: 'Trending VR Apps of {DATE} | Hot VR Games',
        description: 'See what\'s trending in VR right now. Discover the hottest VR apps across platforms based on downloads, engagement, and KeyQuest\'s trending algorithm.',
        keywords: 'trending VR apps, hot VR games, trending VR platform games, popular VR apps now, VR game trends',
        robots: 'index, follow',
    },

    newReleases: {
        title: 'New VR Game Releases of {DATE} | Latest VR Apps',
        description: 'Stay updated with the latest VR game releases across platforms. Discover new apps, upcoming releases, and launch dates.',
        keywords: 'new VR games, new VR releases, latest VR apps, upcoming VR games, VR game launches',
        robots: 'index, follow',
    },

    mostPopular: {
        title: 'Most Popular VR Apps of {DATE} | Top VR Games',
        description: 'Explore the most popular VR apps and games across platforms. Rankings based on downloads, user engagement, and KeyQuest\'s popularity metrics.',
        keywords: 'most popular VR apps, popular VR games, VR app popularity, top VR apps',
        robots: 'index, follow',
    },

    preOrders: {
        title: 'Upcoming VR Games of {DATE} | Pre-Order VR Apps',
        description: 'Discover upcoming VR games available for pre-order across platforms. Get early access to the next generation of VR experiences.',
        keywords: 'upcoming VR games, VR pre-orders, VR app pre-orders, upcoming VR releases, VR game releases',
        robots: 'index, follow',
    },

    weeklyInsights: {
        title: 'Weekly VR Insights of {DATE} | VR Market Trends',
        description: 'Get weekly insights into VR market trends, top-performing apps, and industry analysis. Powered by KeyQuest\'s AI analytics engine.',
        keywords: 'VR market insights, weekly VR trends, VR platform insights, VR industry analysis, VR market data',
        robots: 'index, follow',
    },

    // Custom Insights
    topGrossing: {
        title: 'Top Grossing VR Apps of {DATE} | Highest Revenue VR Games',
        description: 'Discover the top-grossing VR apps and highest revenue VR games across platforms. Track monetization trends and revenue leaders.',
        keywords: 'top grossing VR apps, VR app revenue, VR platform revenue, highest earning VR games',
        robots: 'index, follow',
    },

    topRated: {
        title: 'Top Rated VR Apps of {DATE} | Highest Rated VR Games',
        description: 'Explore the highest-rated VR apps and best-reviewed VR games across platforms. User ratings and reviews from verified players.',
        keywords: 'top rated VR apps, highest rated VR games, best VR app reviews, VR app ratings',
        robots: 'index, follow',
    },

    topDownloaded: {
        title: 'Most Downloaded VR Apps of {DATE} | Top VR App Downloads',
        description: 'See the most downloaded VR apps and top VR games across platforms. Download statistics and popularity rankings updated daily.',
        keywords: 'most downloaded VR apps, VR download stats, VR platform downloads, popular VR downloads',
        robots: 'index, follow',
    },

    topReviewed: {
        title: 'Most Reviewed VR Apps | VR App User Reviews',
        description: 'Discover the most reviewed VR apps with the highest community engagement. User feedback and review volume leaders.',
        keywords: 'most reviewed VR apps, VR app reviews, VR platform reviews, VR user feedback',
        robots: 'index, follow',
    },

    topImproved: {
        title: 'Most Improved VR Apps | Rising VR Games',
        description: 'Track the most improved VR apps showing significant growth. Rising stars and breakout VR games across platforms.',
        keywords: 'most improved VR apps, rising VR games, VR app growth, VR rising stars',
        robots: 'index, follow',
    },

    hits: {
        title: 'VR App Hits | Breakout VR Games',
        description: 'Discover VR app hits and breakout VR games across platforms. Success stories and viral VR experiences.',
        keywords: 'VR app hits, breakout VR games, viral VR apps, VR platform hits, VR success stories',
        robots: 'index, follow',
    },

    allTimeHits: {
        title: 'Top App Score of {DATE} | Best Rated VR Apps',
        description: 'Discover the top-rated VR apps and games with the highest scores. Find the best VR apps across platforms based on user ratings and reviews.',
        keywords: 'top app score, best VR apps, highest rated VR games, VR app ratings, VR app reviews, top scored apps',
        robots: 'index, follow',
    },

    topKeywordsCategories: {
        title: 'Top Keywords & Categories of {DATE} | VR App Store Insights',
        description: 'Discover trending keywords and popular categories in VR apps. Analyze VR app store trends and optimize your VR app visibility.',
        keywords: 'VR keywords, VR categories, VR app store keywords, VR app categories, trending VR keywords, VR app store optimization',
        robots: 'index, follow',
    },

    topDiscounts: {
        title: 'Top VR App Discounts of {DATE} | VR Sales & Price Drops',
        description: 'Find the best deals and price drops on VR apps across platforms. Track discounted games and limited-time sales updated daily.',
        keywords: 'VR app discounts, VR platform sales, VR game deals, VR price drops, discounted VR games',
        robots: 'index, follow',
    },

    creativeInsights: {
        title: 'Creative Insights | VR App Ad Discovery',
        description: 'Explore VR app advertising creatives and ad campaigns across platforms. Discover what\'s working for top-performing VR games.',
        keywords: 'VR ad discovery, VR platform ad creatives, VR app advertising, VR marketing insights, VR ad campaigns',
        robots: 'index, follow',
    },

    support: {
        title: 'Support | KeyQuest Help Center',
        description: 'Get help with KeyQuest\'s VR analytics platform. Submit a support request or track your existing tickets.',
        keywords: 'KeyQuest support, VR analytics help, KeyQuest help center, customer support',
        robots: 'index, follow',
    },

    customInsights: {
        title: 'Custom VR Insights of {DATE} | Top Tracked & Performing VR Apps',
        description: 'Explore custom KeyQuest insights on the best-performing and most-tracked VR apps across platforms, built for deeper app store analysis.',
        keywords: 'custom VR insights, top performing VR apps, most tracked VR apps, VR app analytics, VR app performance',
        robots: 'index, follow',
    },

    topGenres2025: {
        title: 'Top VR Genres of {DATE} | Best Performing VR Categories',
        description: 'See which VR genres are performing best across platforms right now. Track category rankings and discover the top apps in each genre.',
        keywords: 'top VR genres, best VR genres, VR genre rankings, VR category performance, VR game genres',
        robots: 'index, follow',
    },

    blog: {
        title: 'KeyQuest Blog | VR Industry Insights & Trends',
        description: 'Read the latest VR industry insights, gaming platform trends, app store optimization tips, and analytics deep-dives from the KeyQuest team.',
        keywords: 'VR blog, VR industry news, VR industry insights, VR app store optimization, VR analytics blog, gaming platform trends',
        robots: 'index, follow',
    },

    aiCmo: {
        title: 'AI CMO Agent | VR App Marketing & Competitive Analysis',
        description: 'Scan any VR app for AI-powered competitive marketing insights - policy compliance, sentiment analysis, ASO strategy, and ready-to-post social copy.',
        keywords: 'AI marketing agent, VR app marketing, competitive analysis, ASO strategy, VR app compliance, multi-platform marketing, AI CMO',
        robots: 'index, follow',
    },

    keywordSuggestions: {
        title: 'Keyword Suggestions | VR App Keyword Discovery',
        description: 'Find high-performing ASO keywords for any VR app across platforms. Discover top keywords, competition data, and track them from one place.',
        keywords: 'VR keyword suggestions, ASO keyword discovery, VR app keywords, VR app keyword research, keyword competition analysis',
        robots: 'index, follow',
    },

    socialMediaComparison: {
        title: 'Social Media Comparison | VR App Social Presence Analytics',
        description: 'Compare VR apps\' social media presence across YouTube, Instagram, X, TikTok, and Discord, with AI-scored social reach for each app.',
        keywords: 'social media comparison, VR app social presence, multi-platform social analytics, social reach score, VR app social media',
        robots: 'index, follow',
    },

    ugcExplorer: {
        title: 'UGC Explorer | Verified YouTube, Instagram & TikTok Coverage for VR Apps',
        description: 'Discover every verified YouTube, Instagram Reel and TikTok video about a VR app - reviews, gameplay, tutorials and more - each AI-summarized, sentiment-scored, and ranked by views.',
        keywords: 'UGC explorer, user generated content, VR app YouTube coverage, VR app TikTok coverage, VR app Instagram reels, video intelligence, VR app reviews, VR app gameplay videos',
        robots: 'index, follow',
    },

    influencerExplorer: {
        title: 'Influencer Explorer | Cross-Platform VR Creator Directory',
        description: 'Explore every verified TikTok, Instagram and YouTube influencer covering VR and XR apps in one ranked, cross-platform leaderboard - filter by audience size, app coverage, verification and more.',
        keywords: 'influencer explorer, VR influencer directory, XR creator directory, TikTok influencers, Instagram influencers, YouTube influencers, VR app creators, influencer leaderboard',
        robots: 'index, follow',
    },

    // Fallback SEO for one creator's profile screen
    // (/influencer-explorer/:platform/:username) - shown while the profile
    // is loading; InfluencerProfile.jsx overrides the title/description
    // with the creator's own name once INFLUENCER_PROFILE_API.md's response
    // comes back.
    influencerProfile: {
        title: 'Creator Profile | Influencer Explorer',
        description: 'See what this creator has posted about VR and XR apps - covered apps, posts and AI-generated summaries, in one profile.',
        keywords: 'VR influencer profile, XR creator profile, TikTok creator, Instagram creator, YouTube creator, influencer app coverage',
        robots: 'noindex, follow',
    },
};

// Dynamic SEO template generators
export const generateAppSEO = (appName, appData = {}) => ({
    title: `${appName} Analytics | VR App Stats & Insights`,
    description: `Comprehensive analytics for ${appName} across VR and gaming platforms. Track rankings, downloads, reviews, revenue, and performance metrics with KeyQuest's VR app intelligence.`,
    keywords: `${appName}, ${appName} analytics, ${appName} stats, VR app data, ${appName} analytics`,
    robots: 'index, follow',
    structuredData: {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: appName,
        applicationCategory: 'GameApplication',
        ...(appData.price && {
            offers: {
                '@type': 'Offer',
                price: appData.price,
                priceCurrency: 'USD',
            },
        }),
        ...(appData.rating && {
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: appData.rating,
                reviewCount: appData.reviewCount || 0,
            },
        }),
    },
});

export const generatePublisherSEO = (publisherName) => ({
    title: `${publisherName} | VR Game Publisher Analytics`,
    description: `Analyze ${publisherName}'s VR app portfolio. Track publisher performance, app rankings, and market insights across platforms.`,
    keywords: `${publisherName}, VR game publisher, VR app developer, ${publisherName} apps, VR publisher analytics`,
    robots: 'index, follow',
});

export const generateKeywordDetailsSEO = (keyword) => ({
    title: `${keyword} - VR Keyword Analytics & Tracking`,
    description: `Track "${keyword}" keyword performance across platforms. Analyze search volume, competition, ranking apps, and ASO opportunities.`,
    keywords: `${keyword}, VR keyword tracking, ASO keyword, VR app keyword, ${keyword} analytics`,
    robots: 'index, follow',
});

export const generateCategoryDetailsSEO = (category) => ({
    title: `${category} VR Apps | Category Analytics`,
    description: `Explore ${category} VR apps across platforms. Track category rankings, top apps, performance metrics, and trends.`,
    keywords: `${category} VR apps, ${category} VR games, VR ${category} games, ${category} category analytics`,
    robots: 'index, follow',
});

// Breadcrumb generator
export const generateBreadcrumbs = (path) => {
    const pathSegments = path.split('/').filter(Boolean);
    const breadcrumbs = [
        {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_CONFIG.siteUrl,
        },
    ];

    let currentPath = SITE_CONFIG.siteUrl;
    pathSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        breadcrumbs.push({
            '@type': 'ListItem',
            position: index + 2,
            name: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
            item: currentPath,
        });
    });

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
    };
};

// Dynamic robots meta tag generator
export const generateRobotsMeta = (pageType, contentCount = 0, minThreshold = 5) => {
    // User-specific pages should not be indexed
    if (pageType === 'user-tracking' || pageType === 'dashboard' || pageType === 'profile') {
        return 'noindex, follow';
    }

    // Pages with insufficient content should not be indexed
    if (contentCount > 0 && contentCount < minThreshold) {
        return 'noindex, follow';
    }

    // Default: index and follow
    return 'index, follow';
};

// Open Graph (OG) Metadata Configuration
export const OG_METADATA = {
    // Default OG metadata
    default: {
        type: 'website',
        siteName: 'KeyQuest',
        locale: 'en_US',
        image: '/new_web_logo.png',
        imageWidth: 1200,
        imageHeight: 630,
        imageType: 'image/png',
    },

    // Landing Page OG
    landing: {
        title: 'KeyQuest | VR SEO Intelligence Tools Powered by AI & Big Data',
        description: 'Track VR app rankings, analyze performance across VR and gaming platforms, and optimize your app store presence with KeyQuest\'s AI-powered analytics platform.',
        image: '/new_web_logo.png',
        imageAlt: 'KeyQuest VR Analytics Dashboard',
    },

    // Blog Pages OG
    blog: {
        title: 'KeyQuest Blog | VR Industry Insights & Trends',
        description: 'Discover the latest VR industry insights, gaming platform trends, and app store optimization strategies from KeyQuest experts.',
        image: '/new_web_logo.png',
        imageAlt: 'KeyQuest Blog - VR Industry Insights',
    },

    blogDetail: {
        type: 'article',
        author: 'KeyQuest Team',
        section: 'VR Industry',
        tags: ['VR', 'App Store Optimization', 'VR Analytics'],
    },

    // Meta Insights Pages OG
    topApps: {
        title: 'Top VR Apps of {DATE} | Best VR Games',
        description: 'Discover the top-rated VR apps and best VR games across platforms, updated daily with rankings, reviews, and performance data.',
        image: '/new_web_logo.png',
        imageAlt: 'Top VR Apps and VR Games Rankings',
    },

    trending: {
        title: 'Trending VR Apps of {DATE} | Hot VR Games',
        description: 'See what\'s trending in VR right now. Discover the hottest VR apps across platforms based on downloads and engagement.',
        image: '/new_web_logo.png',
        imageAlt: 'Trending VR Apps and Hot VR Games',
    },

    newReleases: {
        title: 'New VR Game Releases of {DATE} | Latest VR Apps',
        description: 'Stay updated with the latest VR game releases across platforms. Discover new apps and upcoming releases.',
        image: '/new_web_logo.png',
        imageAlt: 'New VR Game Releases and Latest VR Apps',
    },

    mostPopular: {
        title: 'Most Popular VR Apps of {DATE} | Top VR Games',
        description: 'Explore the most popular VR apps and games across platforms. Rankings based on downloads, user engagement, and popularity metrics.',
        image: '/new_web_logo.png',
        imageAlt: 'Most Popular VR Apps and Top VR Games',
    },

    // Market Research Pages OG
    keywordSearch: {
        title: 'VR Keyword Research Tool | Multi-Platform ASO',
        description: 'Find the best keywords for your VR app with KeyQuest\'s keyword research tool. Analyze search volume, competition, and trends.',
        image: '/new_web_logo.png',
        imageAlt: 'VR Keyword Research Tool for Multi-Platform ASO',
    },

    categoryAnalysis: {
        title: 'Genre Explorer | VR App Categories',
        description: 'Analyze VR app categories across platforms. Track category rankings, performance metrics, and discover trending categories.',
        image: '/new_web_logo.png',
        imageAlt: 'VR Genre Explorer and App Analysis',
    },

    publisherAnalysis: {
        title: 'Publisher Explorer | VR App Developers',
        description: 'Analyze VR game publishers and developers. Track publisher performance, app portfolios, and market insights.',
        image: '/new_web_logo.png',
        imageAlt: 'VR Publisher Explorer and Developer Analytics',
    },

    // Custom Insights Pages OG
    topGrossing: {
        title: 'Top Grossing VR Apps of {DATE} | Highest Revenue VR Games',
        description: 'Discover the top-grossing VR apps and highest revenue VR games across platforms. Track monetization trends and revenue leaders.',
        image: '/new_web_logo.png',
        imageAlt: 'Top Grossing VR Apps and Highest Revenue VR Games',
    },

    topRated: {
        title: 'Top Rated VR Apps of {DATE} | Highest Rated VR Games',
        description: 'Explore the highest-rated VR apps and best-reviewed VR games across platforms. User ratings and reviews from verified players.',
        image: '/new_web_logo.png',
        imageAlt: 'Top Rated VR Apps and Highest Rated VR Games',
    },

    topDownloaded: {
        title: 'Most Downloaded VR Apps of {DATE} | Top VR App Downloads',
        description: 'See the most downloaded VR apps and top VR games across platforms. Download statistics and popularity rankings updated daily.',
        image: '/new_web_logo.png',
        imageAlt: 'Most Downloaded VR Apps and Top VR App Downloads',
    },

    // Dashboard Pages OG (private)
    dashboard: {
        title: 'KeyQuest Dashboard | Multi-Platform VR & Gaming Analytics',
        description: 'Comprehensive VR app analytics dashboard covering all your VR and gaming platforms. Track rankings, monitor performance, and optimize your app strategy.',
        image: '/new_web_logo.png',
        imageAlt: 'KeyQuest VR Analytics Dashboard',
    },

    // App Detail Pages OG (dynamic)
    appDetail: {
        title: (appName) => `${appName} Analytics | VR App Stats & Insights`,
        description: (appName) => `Comprehensive analytics for ${appName} across VR and gaming platforms. Track rankings, downloads, reviews, and performance metrics.`,
        image: '/new_web_logo.png',
        imageAlt: (appName) => `${appName} VR App Analytics and Performance Metrics`,
    },

    // Publisher Detail Pages OG (dynamic)
    publisherDetail: {
        title: (publisherName) => `${publisherName} | VR Game Publisher Analytics`,
        description: (publisherName) => `Analyze ${publisherName}'s VR app portfolio. Track publisher performance, app rankings, and market insights.`,
        image: '/new_web_logo.png',
        imageAlt: (publisherName) => `${publisherName} VR Game Publisher Portfolio and Analytics`,
    },

    // Keyword Detail Pages OG (dynamic)
    keywordDetail: {
        title: (keyword) => `${keyword} - VR Keyword Analytics & Tracking`,
        description: (keyword) => `Track "${keyword}" keyword performance across platforms. Analyze search volume, competition, ranking apps, and ASO opportunities.`,
        image: '/new_web_logo.png',
        imageAlt: (keyword) => `${keyword} VR Keyword Analytics and Performance Tracking`,
    },

    // Category Detail Pages OG (dynamic)
    categoryDetail: {
        title: (category) => `${category} VR Apps | Category Analytics`,
        description: (category) => `Explore ${category} VR apps across platforms. Track category rankings, top apps, performance metrics, and trends.`,
        image: '/new_web_logo.png',
        imageAlt: (category) => `${category} VR Apps Category Analysis and Rankings`,
    },
};

// Twitter Card Configuration
export const TWITTER_CARD_CONFIG = {
    default: {
        card: 'summary_large_image',
        site: '@keyquest',
        creator: '@keyquest',
    },

    article: {
        card: 'summary_large_image',
        site: '@keyquest',
        creator: '@keyquest',
    },
};

// Function to get OG metadata for a specific page type
export const getOGMetadata = (pageType, dynamicData = {}) => {
    const defaultOG = OG_METADATA.default;
    const pageOG = OG_METADATA[pageType] || {};

    // Merge with defaults and handle dynamic content
    const mergedOG = {
        ...defaultOG,
        ...pageOG,
    };

    // Handle dynamic title/description/imageAlt
    if (typeof mergedOG.title === 'function') {
        mergedOG.title = mergedOG.title(dynamicData.appName || dynamicData.publisherName || dynamicData.keyword || dynamicData.category);
    }
    if (typeof mergedOG.description === 'function') {
        mergedOG.description = mergedOG.description(dynamicData.appName || dynamicData.publisherName || dynamicData.keyword || dynamicData.category);
    }
    if (typeof mergedOG.imageAlt === 'function') {
        mergedOG.imageAlt = mergedOG.imageAlt(dynamicData.appName || dynamicData.publisherName || dynamicData.keyword || dynamicData.category);
    }

    return mergedOG;
};

// Function to get Twitter Card config
export const getTwitterCardConfig = (pageType) => {
    return TWITTER_CARD_CONFIG[pageType] || TWITTER_CARD_CONFIG.default;
};

export default {
    SITE_CONFIG,
    STRUCTURED_DATA,
    PAGE_SEO,
    OG_METADATA,
    TWITTER_CARD_CONFIG,
    generateAppSEO,
    generatePublisherSEO,
    generateKeywordDetailsSEO,
    generateCategoryDetailsSEO,
    generateBreadcrumbs,
    generateRobotsMeta,
    getOGMetadata,
    getTwitterCardConfig,
};
